import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { IncomingMessage } from 'http';
import type { ParsedUrlQuery } from 'querystring';
import { pageResolver } from '../../utils';
import { brandsResolver } from '../../utils/pageResolver';
import { getServersideApi } from '~/features/commerce-api';
import { getStoreList } from '~/services/stores/store-api';
import { getQueryValue } from '~/shared/utils/get-query-value';
import { isDeadProduct } from '~/shared/utils/is-dead-product';
import { fetchTopReviews } from '~/services/yotpo';
import { isSoldOutBeyondDisplayWindow } from '~/shared/utils/is-sold-out-beyond-display-window';
import { SalesforceProduct, SalesforceProductVariant } from '~/lib/salesforce/types';
import { ImageGroup } from '~/lib/data-contract/salesforce';
import { DynamicPageProps } from './DynamicPage';
import { getBaseContent } from '../../utils/getInitialLoadExtraContent';
import { appendQueryParams, containsUpperOrNordicChars, normalizePath } from '~/utils/url';
import { NodeStorageKey, SafeNodeStorage } from '~/services/storage';
import { extractPageSize } from '~/shared/utils/plp';
import { SSR_Data } from '~/lib';
import { generateProductListSSR } from '~/services/instantsearch';

const PRODUCT_ROUTE_PARAMS = ['slug', 'id', 'productRedirect'];

// Keeps tracking params (utm_*, click ids) on a product redirect so the landing page still sees them.
const getForwardedQuery = (query: ParsedUrlQuery): string => {
    const params = new URLSearchParams();

    appendQueryParams(params, query, (key) => PRODUCT_ROUTE_PARAMS.includes(key));

    const queryString = params.toString();

    return queryString ? `&${queryString}` : '';
};

/**
 * Fetch the page and the frame and return as props
 *
 * CacheControl is set based on the response header from the page request
 */
export const getDynamicPageProps = async (
    context: GetServerSidePropsContext,
    storage: SafeNodeStorage,
) => {
    console.time('page-load');

    const headersFromCMS = ['cache-control'];
    const headersToCMS = ['accept-language', 'cf-ipcountry', 'x-forwarded-for', 'user-agent'];

    const { query, req, res, resolvedUrl } = context;
    const api = getServersideApi(storage);

    /**
     * REDIRECT: We will redirect the user to a lowercased path in case their URL's path has an uppercase character.
     * Other parts of the URL will be kept untouched.
     *
     * Ex.: https://imerco.dk/Path-to-Page?Query=1 -> https://imerco.dk/path-to-page?Query=1
     */
    if (containsUpperOrNordicChars(resolvedUrl)) {
        return {
            redirect: {
                permanent: true,
                destination: normalizePath(resolvedUrl),
            },
        };
    }

    const { frame, topSearches } = await getBaseContent(storage);

    const propData: SSR_Data = {
        product: null,
        brands: null,
        plpResult: null,
        storeList: null,
        topSearches,
    };

    /**
     * NOTE: Our app requires Frame data to completely function. If we are unavailable to retrieve it,
     * something may have gone wrong server-side.
     */
    if (!frame) {
        res.statusCode = 500;
        throw new Error('Missing Frame data from Umbraco.');
    }

    const urlAsProductId = resolvedUrl && parseInt(resolvedUrl.split('/')[1], 10);

    let pageUrl = resolvedUrl;
    let topReviewsPromise: ReturnType<typeof fetchTopReviews> | undefined;

    if (query.id || urlAsProductId) {
        const id = query.id?.toString() || urlAsProductId.toString();

        /**
         * Fetch product's top reviews in parallel to fetching product data to reduce load time.
         * `fetchTopReviews` never rejects, so an early return below simply abandons it.
         */
        topReviewsPromise = fetchTopReviews({ productId: id });

        const productResponse = await api.shopperProducts?.getProducts({
            parameters: {
                ids: id,
                currency: frame?.market?.currency?.currency,
                allImages: true,
            },
        });

        // ASSERTION: Shopper Products (commerce-sdk-isomorphic) returns SalesforceProduct.
        const productData = productResponse?.data as SalesforceProduct[] | undefined;

        if (productData) {
            const pageURL = productData[0].c_pageURL;

            if (pageURL) {
                let productUrl = decodeURIComponent(pageURL).toLowerCase();
                if (!productUrl.startsWith('/')) {
                    productUrl = `/${productUrl}`;
                }
                const productRedirect = query.productRedirect;
                const cleanResolved = `/${query.slug}?id=${query.id}`;
                const cleanResolvedFromSlugIssue = `/${query.slug
                    ?.toString()
                    .replace(',', '/')}?id=${query.id}`; //fix for azure changing "/" in the url to ","

                if (!(productUrl === cleanResolved || productUrl === cleanResolvedFromSlugIssue)) {
                    return {
                        redirect: {
                            destination: `${pageURL.toLowerCase()}${productRedirect ? `&productRedirect=${productRedirect}` : ''}${getForwardedQuery(query)}`,
                            permanent: true,
                        },
                    };
                }
            }

            propData.product = productData[0];
            pageUrl = frame?.settings?.pageReferences?.productPage?.url ?? resolvedUrl;
        } else {
            return { redirect: { destination: '/', permanent: true } };
        }
    }

    const storesUrl = frame?.settings?.pageReferences?.storesPage?.url;
    if (storesUrl && Array.isArray(query.slug) && query.slug[0] === storesUrl.slice(1)) {
        try {
            const storeList = await getStoreList(api);
            propData.storeList = storeList;
        } catch {
            return { redirect: { destination: '/', permanent: true } };
        }
    }

    const brandsOverviewPageUrl = frame?.settings?.pageReferences?.brandsOverviewPage?.url;
    if (brandsOverviewPageUrl && resolvedUrl.split('?')[0] === brandsOverviewPageUrl) {
        try {
            const brands = await brandsResolver({
                headers: req.headers,
                forwardHeaders: headersToCMS,
            });
            propData.brands = brands.data;
        } catch (e) {
            return { redirect: { destination: '/', permanent: true } };
        }
    }

    const { data: page, headers } = await pageResolver({
        url: pageUrl,
        headers: req.headers,
        params: propData.product ? getPDPParams(propData.product) : {},
        forwardHeaders: headersToCMS,
    });

    if (!page) {
        throw new Error('Page missing from CMS');
    }

    // Pre-fetch data for product lists on Product List Page and Search Page.
    if (page.type === 'p40ProductListPage' || page.type === 'p10SearchPage') {
        const pageSize = extractPageSize(page, frame);

        const [plpData, storeList] = await Promise.all([
            generateProductListSSR({ req, page, pageSize, query }),
            // We will get a minimal store list to use for PLP filters.
            storage.getIfAvailable(NodeStorageKey.MINIMAL_STORES, {
                fallback: () => getStoreList(api, true),
                lifespanInMinutes: 180,
            }),
        ]);

        if (plpData) propData.plpResult = plpData;
        if (storeList) propData.storeList = storeList;
    }

    if (page.type === 'p40ProductListPage') {
        const productTotalPage =
            propData.plpResult?.serverState.initialResults.products?.results?.map(
                (item) => item.nbPages,
            );

        const currentPage = getQueryValue(query.page);

        if (productTotalPage && currentPage) {
            const rootPage = pageUrl.replace(/(\?.*)|(#.*)/g, '');

            if (parseInt(currentPage) > productTotalPage[0])
                return { redirect: { destination: rootPage, statusCode: 308 } };
        }
    }

    if (page.type === 'p50ProductDetailsPage' && propData.product) {
        const { c_variants } = propData.product;

        const { brand, category, series, subCategory } = page.productCategoryLinks;
        const categoryFallback = subCategory || series || category || brand;

        const product = propData.product;

        /**
         * A product enrolled in the "Display until date" feature must stay visible for its full
         * grace period regardless of any other classification (store format, remainder code,
         * status code) that would otherwise mark it dead. `isDeadProduct` has no awareness of
         * the feature, so it is skipped entirely while the window is still open. Once the window
         * passes, `isDeadProduct` applies again and `isSoldOutBeyondDisplayWindow` takes over the
         * removal decision, based on real stock.
         */
        const isWithinDisplayUntilWindow =
            Boolean(product.c_displayUntilDate) && product.c_isDisplayUntilDate !== false;

        if (
            (!isWithinDisplayUntilWindow && isDeadProduct(product)) ||
            isSoldOutBeyondDisplayWindow(product)
        ) {
            /**
             * Having already redirected to a variant once, do not redirect to one again: some
             * variant ids resolve back to the same underlying product record, which would
             * otherwise loop forever.
             */
            const alreadyRedirectedToVariant = Boolean(query.productRedirect);

            if (!alreadyRedirectedToVariant && c_variants && c_variants.length > 1) {
                // A variant pointing back at the product itself would loop in the same way.
                const variantToRedirect = c_variants.find(
                    (item: SalesforceProductVariant) =>
                        item.orderable === true && item.productId !== product.id,
                );

                if (variantToRedirect) {
                    const param = resolvedUrl.split('?')[0];

                    return {
                        redirect: {
                            destination: `${param}?id=${variantToRedirect.productId}&productRedirect=alternative`,
                            statusCode: 301,
                        },
                    };
                }
            }

            return {
                redirect: {
                    destination: categoryFallback?.url
                        ? `${categoryFallback.url}?productRedirect=${categoryFallback?.title || 'Category'}`
                        : '/',
                    statusCode: 301,
                },
            };
        }

        /**
         * Review bodies for the PDP structured data, started above alongside the product
         * request. Resolves to an empty list on any failure, so this cannot break a render,
         * and products with no reviews are filtered here rather than before the request.
         */
        propData.productTopReviews = propData.product.c_yotpoReviewCount
            ? await topReviewsPromise
            : [];
    }

    // Map headers from page response.
    // Allows the CMS to handle how pages are cached etc.
    headersFromCMS.forEach((headerKey) => {
        const header = headers.get(headerKey);
        header && res.setHeader(headerKey, header);
    });

    if (page.type === 'redirect') {
        const { destination, permanent } = page;
        return { redirect: { destination, permanent } };
    }

    propData.timestamp = Date.now();

    page.data = propData;
    if (req.headers?.host === 'preprod01.imerco.dk') {
        page.meta = page.meta ?? {};
        page.meta.hideFromRobots = true;
    }

    if (propData.product) {
        const { product } = propData;

        const metaImage = extractMetaImage(product);

        page.meta = page.meta ?? {};
        page.meta.url = product.c_pageURL;
        page.meta.title = product.pageTitle ?? '';
        page.meta.description = product.pageDescription ?? '';
        page.meta.keywords = product.pageKeywords ?? '';
        page.meta.canonical = product.c_canonical
            ? { url: product.c_canonical }
            : product.c_pageURL
              ? { url: product.c_pageURL }
              : undefined;
        page.meta.openGraph = page.meta.openGraph ?? {};
        page.meta.openGraph.title = product.pageTitle ?? '';
        page.meta.openGraph.description = product.pageDescription ?? '';
        page.meta.openGraph.type = 'product';
        page.meta.openGraph.image = metaImage
            ? {
                  id: metaImage.images[0]?.title ?? product.pageTitle ?? '',
                  height: 1200,
                  width: 1200,
                  src: metaImage.images[0]?.link,
              }
            : null;

        const brandName = product.brand ? `${product.brand} ` : '';

        page.breadcrumb?.items?.push({
            text: brandName + product.name,
            url: product.c_pageURL,
            title: brandName + product.name,
        });
    } else if (propData.brands) {
        page.type = 'brandsListPage';
    }

    page.isMobile = isRequestFromMobile(req);

    const props: DynamicPageProps = {
        page,
    };

    // Check the status code range
    // add statuscode for the 404 page, if within range. this will make google not index this url/slug
    if (Number(props?.page?.statusCode) >= 400 && Number(props?.page?.statusCode) < 500) {
        res.statusCode = Number(props.page.statusCode);
    }

    console.timeEnd('page-load');

    return {
        props,
    } as GetServerSidePropsResult<DynamicPageProps>;
};

/** The large image group matching the product's own colour and size, for OpenGraph. */
const extractMetaImage = (product: SalesforceProduct): ImageGroup | undefined => {
    if (!product?.imageGroups?.length) return undefined;

    const color = product.variationValues?.supplierColour;
    const size = product.variationValues?.size;

    return product.imageGroups.find((imageGroup: ImageGroup) => {
        if (imageGroup.viewType !== 'large') return false;

        const variationColor = imageGroup.variationAttributes?.[0]?.values?.[0].value;
        const variationSize = imageGroup.variationAttributes?.[1]?.values?.[0].value;

        if (color && size) return variationColor === color && variationSize === size;
        if (color) return variationColor === color;
        if (size) return variationSize === size;

        return false;
    });
};

/** The product fields the CMS needs as query params to resolve the PDP's page content. */
const getPDPParams = (product: SalesforceProduct) => ({
    seriesId: product.c_ProductSeries,
    brandid: product.brand,
    subCategoryId: product.c_ProductMainCategory,
    categoryId: product.c_ProductParentCategory,
});

const isRequestFromMobile = (request: IncomingMessage) => {
    const userAgent = request.headers['user-agent'];

    if (!userAgent) return false;

    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
};
